import React, { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import moment from 'moment';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { editAdminCompanyValidationSchema, ValidationErrors } from 'src/constants/validations';
import { useApplicationsApi } from 'src/modules/applications/api';
import { Application } from 'src/modules/applications/types';
import {
  useAssignTcrCampaignApi,
  useGetAssignmentTaskStatusApi,
  useGetTcrCampaignApi,
  useReactivateTcrCampaignApi,
  useUpdateAdminCompanyApi,
  useUpdateCompanyOnboardingStatusApi,
} from '../api';
import {
  AdminCompany,
  CompanyInfoTabProps,
  CompanyInfoTabViewModel,
  OnboardingStatus,
} from '../types';

const getAssignmentFailureReasonFromCompany = (company: AdminCompany): string | null => {
  const telnyx = company.additionalData?.telnyx;
  if (!telnyx) {
    return null;
  }

  return (
    telnyx.assignmentStatus?.failureReasons ||
    telnyx.assignmentStatus?.error ||
    telnyx.taskStatus?.error ||
    telnyx.error ||
    null
  );
};

const getAssignmentStatusFromCompany = (company: AdminCompany): string | null => {
  return company.additionalData?.telnyx?.assignmentStatus?.status
    || company.additionalData?.telnyx?.taskStatus?.status
    || null;
};

export const useCompanyInfoTabController = (
  params: CompanyInfoTabProps,
): CompanyInfoTabViewModel => {
  const { company, refreshCompany } = params;

  const { getAdminApplications } = useApplicationsApi();
  const updateAdminCompany = useUpdateAdminCompanyApi();
  const updateOnboardingStatus = useUpdateCompanyOnboardingStatusApi();
  const getAssignmentTaskStatus = useGetAssignmentTaskStatusApi();
  const getTcrCampaign = useGetTcrCampaignApi();
  const reactivateTcrCampaign = useReactivateTcrCampaignApi();
  const assignTcrCampaign = useAssignTcrCampaignApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [refreshingStatus, setRefreshingStatus] = useState<boolean>(false);
  const [assigningCampaign, setAssigningCampaign] = useState<boolean>(false);
  const [isUpdatingOnboardingStatus, setIsUpdatingOnboardingStatus] = useState<boolean>(false);
  const [onboardingStatusValue, setOnboardingStatusValue] = useState<OnboardingStatus>(company.onboardingStatus);
  const [committedOnboardingStatus, setCommittedOnboardingStatus] = useState<OnboardingStatus>(company.onboardingStatus);
  const [pendingOnboardingStatus, setPendingOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [isOnboardingStatusDialogOpen, setIsOnboardingStatusDialogOpen] = useState<boolean>(false);
  const [isReactivateInfoDialogOpen, setIsReactivateInfoDialogOpen] = useState<boolean>(false);
  const [assignmentTaskStatus, setAssignmentTaskStatus] = useState({
    taskId: company.additionalData?.telnyx?.taskId || '',
    assignmentStatus: getAssignmentStatusFromCompany(company),
    assignmentFailureReason: getAssignmentFailureReasonFromCompany(company),
  });
  const [refreshingCampaignStatus, setRefreshingCampaignStatus] = useState<boolean>(false);
  const [reactivatingCampaign, setReactivatingCampaign] = useState<boolean>(false);
  const [telnyxCampaignStatus, setTelnyxCampaignStatus] = useState<{
    status: string | null;
    failureReason: string | null;
  }>({ status: null, failureReason: null });

  const [formValues, setFormValues] = useState<AdminCompany>(company);
  const [initialValues, setInitialValues] = useState<AdminCompany>(company);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [applications, setApplications] = useState<Application[]>();

  const timeZones = moment.tz.zonesForCountry('US');
  const onboardingStatuses: OnboardingStatus[] = ['draft', 'processing', 'completed', 'failed'];

  useEffect(() => {
    getAdminApplications({ limit: 10000, offset: 0 }).then(response => {
      if (response.data.items) {
        setApplications(response.data.items);
        setLoading(false);
      }
    });
  }, []);

  const hasFormChanged = () => {
    return (
      formValues.name !== initialValues.name ||
      formValues.application.id !== initialValues.application.id ||
      formValues.timezone !== initialValues.timezone ||
      formValues.messagingProfileId !== initialValues.messagingProfileId ||
      formValues.tcrCampaignId !== initialValues.tcrCampaignId ||
      formValues.manualBilling !== initialValues.manualBilling ||
      formValues.billingEmail !== initialValues.billingEmail ||
      formValues.isDemo !== initialValues.isDemo ||
      formValues.sandBoxMode !== initialValues.sandBoxMode
    );
  };

  useEffect(() => {
    setFormValues(company);
    setInitialValues(company);
    setCommittedOnboardingStatus(company.onboardingStatus);
    setOnboardingStatusValue(company.onboardingStatus);
    setPendingOnboardingStatus(null);
    setIsOnboardingStatusDialogOpen(false);
    setAssignmentTaskStatus({
      taskId: company.additionalData?.telnyx?.taskId || '',
      assignmentStatus: getAssignmentStatusFromCompany(company),
      assignmentFailureReason: getAssignmentFailureReasonFromCompany(company),
    });
  }, [company]);

  useEffect(() => {
    const tcrCampaignId = company.tcrCampaignId;
    const abortController = new AbortController();

    if (!tcrCampaignId) {
      setTelnyxCampaignStatus({ status: null, failureReason: null });
      return () => abortController.abort();
    }

    setTelnyxCampaignStatus({ status: null, failureReason: null });
    getTcrCampaign(tcrCampaignId, { signal: abortController.signal })
      .then(({ data }) => {
        setTelnyxCampaignStatus({
          status: data.status ?? null,
          failureReason: data.failureReasons ?? null,
        });
      })
      .catch((error: { code?: string }) => {
        if (error?.code === 'ERR_CANCELED') {
          return;
        }
        // Ignore initial load errors; user can refresh manually
      });

    return () => abortController.abort();
  }, [company.id, company.tcrCampaignId, getTcrCampaign]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormValues((prevState: AdminCompany) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onChangeApplication = ({ target }: SelectChangeEvent<string>) => {
    const application = applications?.find(a => a.id === target.value);
    if (application) {
      setFormValues({
        ...formValues,
        application: application,
      });
    }
  };

  const onChangeTimezone = ({ target }: SelectChangeEvent<string>) => {
    setFormValues({
      ...formValues,
      timezone: target.value,
    });
  };

  const onChangeManualBilling = (value: string) => {
    setFormValues({
      ...formValues,
      manualBilling: value === 'true',
    });
  };

  const onChangeIsDemo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      isDemo: event.target.checked,
    });
  };

  const onChangeSandBoxMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      sandBoxMode: event.target.checked,
    });
  };

  const onChangeOnboardingStatus = ({ target }: SelectChangeEvent<string>) => {
    const nextStatus = target.value as OnboardingStatus;
    if (nextStatus === committedOnboardingStatus) {
      return;
    }

    setPendingOnboardingStatus(nextStatus);
    setOnboardingStatusValue(nextStatus);
    setIsOnboardingStatusDialogOpen(true);
  };

  const onCancelOnboardingStatusUpdate = () => {
    setPendingOnboardingStatus(null);
    setOnboardingStatusValue(committedOnboardingStatus);
    setIsOnboardingStatusDialogOpen(false);
  };

  const onConfirmOnboardingStatusUpdate = () => {
    if (!pendingOnboardingStatus) {
      return;
    }

    setIsUpdatingOnboardingStatus(true);
    updateOnboardingStatus(company.id, { onboardingStatus: pendingOnboardingStatus })
      .then(async () => {
        setCommittedOnboardingStatus(pendingOnboardingStatus);
        setOnboardingStatusValue(pendingOnboardingStatus);
        try {
          await refreshCompany();
        } catch (refreshError) {
          console.error('Error refreshing company data after onboarding status update:', refreshError);
        }
        toast.success('Account setup status updated');
      })
      .catch((error) => {
        setOnboardingStatusValue(committedOnboardingStatus);
        toast.error('Failed to update account setup status');
        console.error('Error updating onboarding status:', error);
      })
      .finally(() => {
        setPendingOnboardingStatus(null);
        setIsOnboardingStatusDialogOpen(false);
        setIsUpdatingOnboardingStatus(false);
      });
  };

  const validate = (values: AdminCompany) => {
    try {
      editAdminCompanyValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const isValidForm = (values: AdminCompany) => {
    try {
      editAdminCompanyValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      return true;
    } catch {
      return false;
    }
  };

  const onSave = () => {
    const isValid = validate(formValues);
    if (isValid) {
      updateAdminCompany(
        {
          name: formValues.name,
          applicationId: formValues.application.id,
          timezone: formValues.timezone,
          messagingProfileId: formValues.messagingProfileId,
          tcrCampaignId: formValues.tcrCampaignId,
          manualBilling: formValues.manualBilling,
          billingEmail: formValues.billingEmail,
          isDemo: formValues.isDemo,
          sandBoxMode: formValues.sandBoxMode,
        },
        formValues.id,
      ).then(() => {
        refreshCompany();
        setInitialValues(formValues);
        toast.success('company info is updated');

        // Refresh the page if sandBoxMode has changed to update Header visualization
        if (initialValues.sandBoxMode !== formValues.sandBoxMode) {
          window.location.reload();
        }
      });
    }
  };

  const onRefreshAssignmentStatus = () => {
    setRefreshingStatus(true);
    getAssignmentTaskStatus(company.id)
      .then(({ data }) => {
        setAssignmentTaskStatus({
          taskId: data.taskId || '',
          assignmentStatus: data.assignmentStatus ?? data.taskStatus,
          assignmentFailureReason: data.assignmentStatusFailure ?? data.assignmentFailureReasons ?? null,
        });
        toast.success('Assignment status updated');
      })
      .catch((error) => {
        toast.error('Failed to refresh assignment status');
        console.error('Error refreshing assignment status:', error);
      })
      .finally(() => {
        setRefreshingStatus(false);
      });
  };

  const onRefreshTelnyxCampaignStatus = () => {
    const tcrCampaignId = formValues.tcrCampaignId || company.tcrCampaignId;
    if (!tcrCampaignId) {
      return;
    }
    setRefreshingCampaignStatus(true);
    getTcrCampaign(tcrCampaignId)
      .then(({ data }) => {
        setTelnyxCampaignStatus({
          status: data.status ?? null,
          failureReason: data.failureReasons ?? null,
        });
        toast.success('Telnyx campaign status updated');
      })
      .catch((error) => {
        toast.error('Failed to refresh Telnyx campaign status');
        console.error('Error refreshing Telnyx campaign status:', error);
      })
      .finally(() => {
        setRefreshingCampaignStatus(false);
      });
  };

  const onAssignTcrCampaign = () => {
    setAssigningCampaign(true);
    assignTcrCampaign(company.id)
      .then(() => {
        // Update the company data by calling refreshCompany to get the latest data
        refreshCompany();
        toast.success('TCR campaign assignment initiated');
      })
      .catch((error) => {
        toast.error('Failed to assign TCR campaign');
        console.error('Error assigning TCR campaign:', error);
      })
      .finally(() => {
        setAssigningCampaign(false);
      });
  };

  const onReactivateTcrCampaign = () => {
    const tcrCampaignId = formValues.tcrCampaignId || company.tcrCampaignId;
    if (!tcrCampaignId) {
      return;
    }

    setReactivatingCampaign(true);
    reactivateTcrCampaign(tcrCampaignId)
      .then(({ data }) => {
        setTelnyxCampaignStatus({
          status: data.status ?? null,
          failureReason: data.failureReasons ?? null,
        });
        setIsReactivateInfoDialogOpen(true);
      })
      .catch((error: unknown) => {
        const failureReason = getApiErrorMessage(error, 'Failed to reactivate Telnyx campaign');
        setTelnyxCampaignStatus(prevState => ({
          ...prevState,
          failureReason,
        }));
        toast.error('Failed to reactivate Telnyx campaign');
        console.error('Error reactivating Telnyx campaign:', error);
      })
      .finally(() => {
        setReactivatingCampaign(false);
      });
  };

  const onCloseReactivateInfoDialog = () => {
    setIsReactivateInfoDialogOpen(false);
  };

  const assignmentStatusFailure = assignmentTaskStatus.assignmentFailureReason;
  const telnyxCampaignFailure = telnyxCampaignStatus.failureReason;
  const hasTcrCampaignId = Boolean(formValues.tcrCampaignId || company.tcrCampaignId);
  const canReactivateTcrCampaign = hasTcrCampaignId && telnyxCampaignStatus.status === 'TCR_SUSPENDED';

  return {
    loading,
    applications,
    formValues,
    initialValues,
    errors,
    timeZones,
    onboardingStatuses,
    onboardingStatusValue,
    isUpdatingOnboardingStatus,
    isOnboardingStatusDialogOpen,
    isReactivateInfoDialogOpen,
    assigningCampaign,
    refreshingStatus,
    refreshingCampaignStatus,
    reactivatingCampaign,
    assignmentTaskStatus,
    telnyxCampaignStatus,
    assignmentStatusFailure,
    telnyxCampaignFailure,
    hasTcrCampaignId,
    canReactivateTcrCampaign,
    hasFormChanged,
    isValidForm,
    onChange,
    onChangeApplication,
    onChangeTimezone,
    onChangeManualBilling,
    onChangeIsDemo,
    onChangeSandBoxMode,
    onChangeOnboardingStatus,
    onCancelOnboardingStatusUpdate,
    onConfirmOnboardingStatusUpdate,
    onSave,
    onRefreshAssignmentStatus,
    onRefreshTelnyxCampaignStatus,
    onAssignTcrCampaign,
    onReactivateTcrCampaign,
    onCloseReactivateInfoDialog,
  };
};
